"use client";

import { useEffect } from "react";

const BAR_COLOR = "#173f5f";
const BAR_HEIGHT = 4;
const SPEED = 200;
const TRICKLE_INTERVAL = 200;
const MIN = 0.08;
const Z_INDEX = 99999;
const BAR_ID = "navigation-progress";

let status: number | null = null;
let trickleTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Forces a synchronous browser reflow so pending style changes are
 * committed before the next style mutation (required for CSS transitions
 * to animate from the current position).
 *
 * @param el - The element to force reflow on.
 */
function forceReflow(el: HTMLElement): void {
  el.getBoundingClientRect();
}

/**
 * Clamps a number between a minimum and maximum value.
 *
 * @param n - The number to clamp.
 * @param min - The minimum value.
 * @param max - The maximum value.
 * @returns The clamped number.
 */
function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(n, max));
}

/**
 * Returns the progress bar DOM element, or null if it doesn't exist.
 *
 * @returns The bar element or null.
 */
function getBar(): HTMLDivElement | null {
  return document.getElementById(BAR_ID) as HTMLDivElement | null;
}

/**
 * Creates or returns the progress bar DOM element.
 *
 * @returns The bar element.
 */
function renderBar(): HTMLDivElement {
  let bar = getBar();
  if (bar) return bar;

  bar = document.createElement("div");
  bar.id = BAR_ID;
  bar.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: ${String(BAR_HEIGHT)}px;
    z-index: ${String(Z_INDEX)};
    pointer-events: none;
    transition: none;
  `;

  const inner = document.createElement("div");
  inner.style.cssText = `
    height: 100%;
    background: ${BAR_COLOR};
    transition: all ${String(SPEED)}ms linear;
    transform: translate3d(-100%, 0, 0);
  `;
  inner.dataset.role = "bar";

  const peg = document.createElement("div");
  peg.style.cssText = `
    display: block;
    position: absolute;
    right: 0;
    width: 100px;
    height: 100%;
    box-shadow: 0 0 10px ${BAR_COLOR}, 0 0 5px ${BAR_COLOR};
    opacity: 1;
    transform: rotate(3deg) translate(0px, -4px);
  `;
  inner.appendChild(peg);
  bar.appendChild(inner);
  document.body.appendChild(bar);

  return bar;
}

/**
 * Sets the progress bar to a specific position between 0 and 1.
 *
 * @param n - The target progress value.
 */
function setProgress(n: number): void {
  n = clamp(n, MIN, 1);
  status = n === 1 ? null : n;

  const bar = renderBar();
  const inner = bar.querySelector<HTMLDivElement>("[data-role='bar']");
  if (!inner) return;

  forceReflow(bar);

  inner.style.transition = `all ${String(SPEED)}ms linear`;
  inner.style.transform = `translate3d(${String((-1 + n) * 100)}%, 0, 0)`;

  if (n === 1) {
    setTimeout(() => {
      bar.style.transition = `opacity ${String(SPEED)}ms linear`;
      bar.style.opacity = "0";
      setTimeout(() => {
        bar.remove();
      }, SPEED);
    }, SPEED);
  }
}

/**
 * Returns a random increment amount based on the current progress.
 * Slows down as progress increases to simulate realistic loading.
 *
 * @param n - The current progress value.
 * @returns The random increment amount.
 */
function randomInc(n: number): number {
  if (n >= 0 && n < 0.2) return 0.1;
  if (n >= 0.2 && n < 0.5) return 0.04;
  if (n >= 0.5 && n < 0.8) return 0.02;
  if (n >= 0.8 && n < 0.99) return 0.005;

  return 0;
}

/**
 * Starts the trickle animation that incrementally advances the bar.
 */
function startTrickle(): void {
  if (trickleTimer) clearTimeout(trickleTimer);
  trickleTimer = setTimeout(function tick() {
    if (status === null) return;
    setProgress(status + Math.random() * randomInc(status));
    trickleTimer = setTimeout(tick, TRICKLE_INTERVAL);
  }, TRICKLE_INTERVAL);
}

/**
 * Module-level progress bar API for programmatic control.
 * Used by `useProgressRouter` to trigger the bar on `router.push()` calls,
 * since anchor-based detection only covers `<Link>` / `<a>` clicks.
 */
export const progress = {
  /**
   * Starts the progress bar animation.
   */
  start(): void {
    if (status !== null) return;
    setProgress(MIN);
    startTrickle();
  },

  /**
   * Completes the progress bar animation and removes it.
   */
  done(): void {
    if (status === null) return;
    if (trickleTimer) {
      clearTimeout(trickleTimer);
      trickleTimer = null;
    }
    setProgress(1);
  },
};

/**
 * Determines whether a click on an anchor element should trigger the progress bar.
 *
 * @param anchor - The anchor element that was clicked.
 * @param event - The mouse event.
 * @returns Whether the navigation should be tracked.
 */
function shouldTrackAnchor(
  anchor: HTMLAnchorElement,
  event: MouseEvent,
): boolean {
  if (event.defaultPrevented) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
    return false;
  if (anchor.target === "_blank") return false;
  if (anchor.hasAttribute("download")) return false;

  const href = anchor.href;
  if (!href) return false;

  try {
    const url = new URL(href);
    if (url.protocol !== "http:" && url.protocol !== "https:") return false;
  } catch {
    return false;
  }

  if (!href.startsWith(location.origin)) return false;

  const target = new URL(href);
  const current = new URL(location.href);
  if (target.href === current.href) return false;

  return true;
}

/**
 * Self-contained navigation progress bar for Next.js App Router.
 *
 * Renders a thin animated bar at the top of the page during navigation.
 * Detection works by listening for anchor clicks via event delegation (starts the bar),
 * proxying `window.history.pushState`/`replaceState` (stops when navigation commits),
 * and listening for `popstate`/`hashchange` events.
 *
 * For programmatic `router.push()` calls, use the exported `progress` singleton
 * via the `useProgressRouter` hook.
 *
 * @returns `null` — this component only registers side effects.
 */
export default function NavigationProgress(): null {
  useEffect(() => {
    const handleClick = (event: MouseEvent): void => {
      const anchor = (event.target as HTMLElement).closest("a");
      if (anchor && shouldTrackAnchor(anchor, event)) {
        progress.start();
      }
    };

    document.addEventListener("click", handleClick, { capture: true });

    const nativePushState = window.history.pushState.bind(window.history);
    const nativeReplaceState = window.history.replaceState.bind(window.history);
    const wrappedPushState = (
      ...args: Parameters<typeof window.history.pushState>
    ): void => {
      progress.done();
      nativePushState(...args);
    };
    const wrappedReplaceState = (
      ...args: Parameters<typeof window.history.replaceState>
    ): void => {
      progress.done();
      nativeReplaceState(...args);
    };
    window.history.pushState = wrappedPushState;
    window.history.replaceState = wrappedReplaceState;

    const handlePopState = (): void => {
      progress.done();
    };
    const handleHashChange = (): void => {
      progress.done();
    };
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      document.removeEventListener("click", handleClick, { capture: true });
      if (window.history.pushState === wrappedPushState) {
        window.history.pushState = nativePushState;
      }
      if (window.history.replaceState === wrappedReplaceState) {
        window.history.replaceState = nativeReplaceState;
      }
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  return null;
}
